"use client";

import {
  ActionCard,
  AgentChatMessage,
  AgentSessionState,
  ChatBubble,
  ChatInput,
  ProgressBar,
  QuickReplies,
  TypingIndicator,
} from "@/components/agent";
import { ONBOARDING_TOTAL_STEPS } from "@/lib/agent/types";
import { trackBeginCheckout, trackPaywallView } from "@/lib/analytics/gtm-datalayer";
import { RotateCcw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "chicky_onboarding_agent_v1";

interface StartChatResponse {
  session_id: string;
  message: string;
  quick_replies?: string[];
  action?: AgentChatMessage["action"];
  step?: number;
  extracted_data?: Record<string, string>;
}

interface SendChatResponse {
  message: string;
  quick_replies?: string[];
  action?: AgentChatMessage["action"];
  step?: number;
  extracted_data?: Record<string, string>;
}

function normalizeQuickReplies(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
}

function clampStep(value: number | undefined): number {
  if (!value || !Number.isFinite(value)) {
    return 1;
  }
  return Math.min(Math.max(Math.round(value), 1), ONBOARDING_TOTAL_STEPS);
}

function normalizeChatMessageArray(value: unknown): AgentChatMessage[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter(
      (entry): entry is AgentChatMessage =>
        typeof entry === "object" &&
        entry !== null &&
        (entry as AgentChatMessage).role !== undefined &&
        typeof (entry as AgentChatMessage).content === "string"
    )
    .map((entry) => ({
      role: entry.role === "assistant" ? "assistant" : "user",
      content: entry.content,
      action: entry.action ?? null,
      quick_replies: normalizeQuickReplies(entry.quick_replies),
      step: entry.step ? clampStep(entry.step) : undefined,
    }));
}

function normalizeCollectedData(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object") {
    return {};
  }

  const out: Record<string, string> = {};
  for (const [key, rawVal] of Object.entries(value as Record<string, unknown>)) {
    if (!key.trim()) {
      continue;
    }
    const clean = String(rawVal ?? "").trim();
    if (!clean) {
      continue;
    }
    out[key] = clean;
  }
  return out;
}

export function AgentChatPage() {
  const [messages, setMessages] = useState<AgentChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [latestQuickReplies, setLatestQuickReplies] = useState<string[]>([]);
  const [collectedData, setCollectedData] = useState<Record<string, string>>({});
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const hasTrackedPaymentCardRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    window.setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 80);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  useEffect(() => {
    if (
      hasTrackedPaymentCardRef.current ||
      !messages.some((message) => message.action === "show_payment")
    ) {
      return;
    }

    hasTrackedPaymentCardRef.current = true;
    trackPaywallView({
      source: "onboarding_agent",
      surface: "agent_payment_card",
      userType: "guest",
    });
  }, [messages]);

  const startNewChat = useCallback(async () => {
    setIsTyping(true);
    try {
      const response = await fetch("/api/agent/chat/start", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to start onboarding session.");
      }

      const data = (await response.json()) as StartChatResponse;
      const firstMessage: AgentChatMessage = {
        role: "assistant",
        content: data.message,
        action: data.action ?? null,
        quick_replies: normalizeQuickReplies(data.quick_replies),
        step: data.step ? clampStep(data.step) : 1,
      };

      setSessionId(data.session_id);
      setMessages([firstMessage]);
      setCurrentStep(firstMessage.step || 1);
      setLatestQuickReplies(firstMessage.quick_replies || []);
      setCollectedData(normalizeCollectedData(data.extracted_data));
    } catch {
      setMessages([
        {
          role: "assistant",
          content: "I could not start the onboarding right now. Please refresh and try again.",
          quick_replies: [],
          action: null,
          step: 1,
        },
      ]);
      setSessionId(null);
      setCurrentStep(1);
      setLatestQuickReplies([]);
      setCollectedData({});
    } finally {
      setInitialLoading(false);
      setIsTyping(false);
    }
  }, []);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        void startNewChat();
        return;
      }

      const parsed = JSON.parse(raw) as Partial<AgentSessionState>;
      if (!parsed.sessionId) {
        window.localStorage.removeItem(STORAGE_KEY);
        void startNewChat();
        return;
      }

      const restoredMessages = normalizeChatMessageArray(parsed.messages);
      if (!restoredMessages.length) {
        window.localStorage.removeItem(STORAGE_KEY);
        void startNewChat();
        return;
      }

      setSessionId(parsed.sessionId);
      setMessages(restoredMessages);
      setCurrentStep(clampStep(parsed.currentStep));
      setLatestQuickReplies(normalizeQuickReplies(parsed.latestQuickReplies));
      setCollectedData(normalizeCollectedData(parsed.collectedData));
      setInitialLoading(false);
    } catch {
      window.localStorage.removeItem(STORAGE_KEY);
      void startNewChat();
    }
  }, [startNewChat]);

  useEffect(() => {
    if (!sessionId || !messages.length) {
      return;
    }

    const state: AgentSessionState = {
      sessionId,
      messages,
      currentStep,
      latestQuickReplies,
      collectedData,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [sessionId, messages, currentStep, latestQuickReplies, collectedData]);

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || !sessionId || isTyping) {
      return;
    }

    const history = [...messages];
    const userMessage: AgentChatMessage = { role: "user", content: trimmed };

    setMessages((prev) => [...prev, userMessage]);
    setLatestQuickReplies([]);
    setIsTyping(true);

    try {
      const response = await fetch("/api/agent/chat/message", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: trimmed,
          messages: history,
          current_step: currentStep,
          collected_data: collectedData,
        }),
      });

      if (!response.ok) {
        throw new Error("Message request failed.");
      }

      const data = (await response.json()) as SendChatResponse;
      const assistantMessage: AgentChatMessage = {
        role: "assistant",
        content: data.message,
        action: data.action ?? null,
        quick_replies: normalizeQuickReplies(data.quick_replies),
        step: data.step ? clampStep(data.step) : currentStep,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setCurrentStep(assistantMessage.step || currentStep);
      setLatestQuickReplies(assistantMessage.quick_replies || []);
      setCollectedData((prev) => ({
        ...prev,
        ...normalizeCollectedData(data.extracted_data),
      }));
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Oops, something went wrong on my end. Please send that again.",
          action: null,
          quick_replies: [],
          step: currentStep,
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handlePayment = async () => {
    if (!sessionId) {
      return;
    }

    try {
      const response = await fetch("/api/agent/payments/create-checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: sessionId,
          origin_url: window.location.origin,
          collected_data: collectedData,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create checkout session.");
      }

      const data = (await response.json()) as { url?: string };
      if (!data.url) {
        throw new Error("Checkout URL missing.");
      }

      trackBeginCheckout({
        value: 300,
        currency: "EUR",
        surface: "agent_payment_card",
        paywallSource: "onboarding_agent",
        userType: "guest",
        items: [
          {
            itemId: "cohort_program_3_month",
            itemName: "ChickyTutor Cohort Program",
            price: 300,
          },
        ],
      });

      window.location.href = data.url;
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Checkout could not be opened right now. Please try again in a moment.",
          action: null,
          quick_replies: [],
          step: currentStep,
        },
      ]);
    }
  };

  const handleNewChat = () => {
    window.localStorage.removeItem(STORAGE_KEY);
    setMessages([]);
    setSessionId(null);
    setCurrentStep(1);
    setLatestQuickReplies([]);
    setCollectedData({});
    hasTrackedPaymentCardRef.current = false;
    setInitialLoading(true);
    void startNewChat();
  };

  if (initialLoading) {
    return (
      <div
        data-testid="loading-screen"
        className="agent-noise-bg flex h-screen items-center justify-center bg-[#FFFDF5]"
      >
        <div className="agent-animate-fade-up text-center">
          <img
            src="https://www.chickytutor.com/ui/Chickylogovector.gif?v=0"
            alt="Chicky"
            className="mx-auto mb-6 h-20 w-20 rounded-full border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
          />
          <p className="text-xl font-black uppercase tracking-tight">Loading Chicky...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="chat-page"
      className="agent-noise-bg flex h-screen flex-col overflow-hidden bg-[#FFFDF5]"
    >
      <header className="z-10 shrink-0 border-b-2 border-black bg-[#FFFDF5] px-4 py-3">
        <div className="mx-auto max-w-2xl">
          <div className="mb-3 flex items-center gap-3">
            <img
              src="https://www.chickytutor.com/ui/Chickylogovector.gif?v=0"
              alt="Chicky"
              className="h-10 w-10 rounded-full border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
            />
            <div className="min-w-0 flex-1">
              <h1
                data-testid="app-title"
                className="text-lg font-black uppercase leading-tight tracking-tight"
              >
                CHICKYTUTOR
              </h1>
              <p className="text-xs font-bold uppercase tracking-widest opacity-60">
                Onboarding Agent
              </p>
            </div>
            <button
              data-testid="new-chat-button"
              onClick={handleNewChat}
              className="flex items-center gap-1.5 rounded-xl border-2 border-black bg-white px-3 py-2 text-sm font-bold text-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-y-0 active:shadow-none"
            >
              <RotateCcw size={14} strokeWidth={3} />
              <span className="hidden sm:inline">NEW</span>
            </button>
          </div>
          <ProgressBar step={currentStep} total={ONBOARDING_TOTAL_STEPS} />
        </div>
      </header>

      <main className="flex-1 overflow-y-auto" data-testid="messages-container">
        <div className="mx-auto max-w-2xl space-y-5 px-4 py-6">
          {messages.map((message, index) => (
            <div key={`${message.role}-${index}-${message.content.slice(0, 24)}`}>
              <ChatBubble role={message.role} content={message.content} index={index} />
              {message.role === "assistant" && message.action && (
                <div className="mt-3">
                  <ActionCard action={message.action} onPayment={handlePayment} />
                </div>
              )}
            </div>
          ))}
          {isTyping && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>
      </main>

      <footer className="shrink-0 border-t-2 border-black bg-[#FFFDF5]">
        <div className="mx-auto max-w-2xl space-y-3 px-4 py-3">
          {latestQuickReplies.length > 0 && !isTyping && (
            <QuickReplies options={latestQuickReplies} onSelect={sendMessage} />
          )}
          <ChatInput onSend={sendMessage} disabled={isTyping} />
        </div>
      </footer>
    </div>
  );
}
